


















































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import {
  filterMentions,
  getMentionDef,
  mentionDisplayLabel,
  MentionItem,
  parseMentionText,
  serializeMentionSegments,
  TextSegment,
  visibleMentionLength,
} from '@/components/simpleSlide/financialMentions';

@Component
export default class FinancialMentionField extends Vue {
  @Prop({ default: '' }) readonly value!: string;
  @Prop({ default: null }) readonly companyData!: any;
  @Prop({ default: false }) readonly multiline!: boolean;
  @Prop({ default: 0 }) readonly maxlength!: number;
  @Prop({ default: false }) readonly disabled!: boolean;
  @Prop({ default: '' }) readonly placeholder!: string;
  @Prop({ default: '' }) readonly inputClass!: string;
  @Prop({ default: false }) readonly showHint!: boolean;

  pickerOpen: boolean = false;
  query: string = '';
  selectedIndex: number = 0;
  items: MentionItem[] = [];
  pickerStyle: Record<string, string> = {};

  private lastEmitted: string = '';
  private syncing: boolean = false;
  private mentionRange: Range | null = null;

  mounted() {
    this.renderFromValue(this.value || '');
    this.lastEmitted = this.value || '';
  }

  @Watch('value')
  onValueProp(val: string) {
    if (val === this.lastEmitted) {
      return;
    }
    this.renderFromValue(val || '');
    this.lastEmitted = val || '';
  }

  @Watch('companyData', { deep: true })
  onDataChanged() {
    this.renderFromValue(this.value || '');
    if (this.pickerOpen) {
      this.refreshItems();
    }
  }

  get groupedItems(): Array<{ category: string; items: MentionItem[] }> {
    const order = [
      'Bolagsinformation',
      'Resultatpost',
      'Balanspost',
      'Kassaflödesterm',
      'Nyckeltal',
    ];
    const map: Record<string, MentionItem[]> = {};
    for (const item of this.items) {
      if (!map[item.category]) {
        map[item.category] = [];
      }
      map[item.category].push(item);
    }
    return order
      .filter((c) => map[c] && map[c].length)
      .map((c) => ({ category: c, items: map[c] }));
  }

  flatIndex(group: { category: string; items: MentionItem[] }, idx: number): number {
    let n = 0;
    for (const g of this.groupedItems) {
      if (g.category === group.category) {
        return n + idx;
      }
      n += g.items.length;
    }
    return n + idx;
  }

  flatItem(index: number): MentionItem | null {
    let n = 0;
    for (const g of this.groupedItems) {
      for (const item of g.items) {
        if (n === index) {
          return item;
        }
        n++;
      }
    }
    return null;
  }

  renderFromValue(text: string) {
    const editor = this.$refs.editor as HTMLElement | undefined;
    if (!editor) {
      return;
    }
    this.syncing = true;
    const segments = parseMentionText(text);
    editor.innerHTML = '';
    if (!segments.length) {
      // Keep a br so empty contenteditable has a caret target in multiline
      if (this.multiline) {
        editor.appendChild(document.createElement('br'));
      }
    } else {
      for (const seg of segments) {
        if (seg.type === 'text') {
          editor.appendChild(document.createTextNode(seg.value));
        } else {
          editor.appendChild(this.createChip(seg.id));
        }
      }
    }
    this.$nextTick(() => {
      this.syncing = false;
    });
  }

  createChip(id: string): HTMLSpanElement {
    const def = getMentionDef(id);
    const label = def ? def.name : id;
    const span = document.createElement('span');
    span.className = 'fm-chip';
    span.contentEditable = 'false';
    span.dataset.mentionId = id;
    span.dataset.label = label;
    span.title = label;
    span.textContent = mentionDisplayLabel(id, this.companyData);
    return span;
  }

  serialize(): string {
    const editor = this.$refs.editor as HTMLElement;
    const segments: TextSegment[] = [];
    const walk = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const t = node.textContent || '';
        if (t) {
          segments.push({ type: 'text', value: t });
        }
        return;
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        if (el.dataset && el.dataset.mentionId) {
          segments.push({ type: 'mention', id: el.dataset.mentionId });
          return;
        }
        if (el.tagName === 'BR') {
          segments.push({ type: 'text', value: '\n' });
          return;
        }
        if (el.tagName === 'DIV' || el.tagName === 'P') {
          const prev = segments.length ? segments[segments.length - 1] : null;
          const prevEndsNewline =
            prev !== null &&
            prev.type === 'text' &&
            (prev as { value: string }).value.endsWith('\n');
          if (segments.length && !prevEndsNewline) {
            // New block = newline (except first)
            if (editor.childNodes[0] !== el) {
              segments.push({ type: 'text', value: '\n' });
            }
          }
        }
        for (const child of Array.from(el.childNodes)) {
          walk(child);
        }
      }
    };
    for (const child of Array.from(editor.childNodes)) {
      walk(child);
    }
    // Collapse trailing newline from lone <br>
    let serialized = serializeMentionSegments(segments);
    if (serialized === '\n') {
      serialized = '';
    }
    return serialized;
  }

  emitValue() {
    const serialized = this.serialize();
    this.lastEmitted = serialized;
    this.$emit('input', serialized);
  }

  onInput() {
    if (this.syncing) {
      return;
    }
    if (this.maxlength > 0) {
      const visible = visibleMentionLength(this.serialize(), this.companyData);
      if (visible > this.maxlength) {
        // Revert to last good value
        this.renderFromValue(this.lastEmitted);
        this.placeCaretAtEnd();
        return;
      }
    }
    this.emitValue();
    this.updatePickerFromCaret();
  }

  onKeyDown(e: KeyboardEvent) {
    if (this.disabled) {
      return;
    }
    if (this.pickerOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.selectedIndex = (this.selectedIndex + 1) % Math.max(this.items.length, 1);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.selectedIndex =
          (this.selectedIndex + this.items.length - 1) % Math.max(this.items.length, 1);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const item = this.flatItem(this.selectedIndex);
        if (item) {
          this.selectItem(item);
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        this.closePicker();
        return;
      }
    }
    if (!this.multiline && e.key === 'Enter') {
      e.preventDefault();
    }
  }

  onKeyUp() {
    if (!this.pickerOpen) {
      this.updatePickerFromCaret();
    }
  }

  onClick() {
    this.updatePickerFromCaret();
  }

  onBlur() {
    // Delay so mousedown on picker can fire first
    setTimeout(() => {
      this.closePicker();
    }, 150);
  }

  onPaste(e: ClipboardEvent) {
    e.preventDefault();
    const text = e.clipboardData ? e.clipboardData.getData('text/plain') : '';
    const cleaned = this.multiline ? text : text.replace(/\n/g, ' ');
    document.execCommand('insertText', false, cleaned);
  }

  updatePickerFromCaret() {
    const info = this.getHashQueryAtCaret();
    if (!info) {
      this.closePicker();
      return;
    }
    this.query = info.query;
    this.mentionRange = info.range;
    this.refreshItems();
    this.pickerOpen = true;
    this.selectedIndex = 0;
    this.$nextTick(() => this.positionPicker());
  }

  refreshItems() {
    this.items = filterMentions(this.query, this.companyData);
  }

  getHashQueryAtCaret(): { query: string; range: Range } | null {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) {
      return null;
    }
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE) {
      return null;
    }
    // Do not trigger inside chips
    const parent = node.parentElement;
    if (parent && parent.dataset && parent.dataset.mentionId) {
      return null;
    }
    const text = node.textContent || '';
    const offset = range.startOffset;
    const before = text.slice(0, offset);
    const hashIdx = before.lastIndexOf('#');
    if (hashIdx < 0) {
      return null;
    }
    // # must start a token (start or after whitespace)
    if (hashIdx > 0 && !/\s/.test(before[hashIdx - 1])) {
      return null;
    }
    const query = before.slice(hashIdx + 1);
    // Abort if query contains a newline
    if (query.indexOf('\n') >= 0) {
      return null;
    }
    const r = document.createRange();
    r.setStart(node, hashIdx);
    r.setEnd(node, offset);
    return { query, range: r };
  }

  positionPicker() {
    const editor = this.$refs.editor as HTMLElement;
    if (!editor || !this.mentionRange) {
      return;
    }
    const rect = this.mentionRange.getBoundingClientRect();
    const root = this.$el as HTMLElement;
    const rootRect = root.getBoundingClientRect();
    this.pickerStyle = {
      top: rect.bottom - rootRect.top + 4 + 'px',
      left: Math.max(0, rect.left - rootRect.left) + 'px',
    };
  }

  selectItem(item: MentionItem) {
    if (!this.mentionRange) {
      return;
    }
    const projected =
      visibleMentionLength(this.serialize(), this.companyData) -
      this.mentionRange.toString().length +
      mentionDisplayLabel(item.id, this.companyData).length;
    if (this.maxlength > 0 && projected > this.maxlength) {
      this.closePicker();
      return;
    }

    const sel = window.getSelection();
    sel!.removeAllRanges();
    sel!.addRange(this.mentionRange);
    this.mentionRange.deleteContents();

    const chip = this.createChip(item.id);
    const space = document.createTextNode(' ');
    const frag = document.createDocumentFragment();
    frag.appendChild(chip);
    frag.appendChild(space);
    this.mentionRange.insertNode(frag);

    // Place caret after the space
    const after = document.createRange();
    after.setStartAfter(space);
    after.collapse(true);
    sel!.removeAllRanges();
    sel!.addRange(after);

    this.closePicker();
    this.emitValue();
  }

  closePicker() {
    this.pickerOpen = false;
    this.query = '';
    this.mentionRange = null;
    this.items = [];
  }

  placeCaretAtEnd() {
    const editor = this.$refs.editor as HTMLElement;
    const sel = window.getSelection();
    if (!editor || !sel) {
      return;
    }
    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
  }
}
